import React from 'react';
import type { WxbStatusTone } from '../StatusBadge/StatusBadge';
import './CalendarStrip.css';

export interface WxbCalendarStripEvent {
  key: string;
  label: React.ReactNode;
  tone?: WxbStatusTone;
}

export interface WxbCalendarStripDay {
  key: string;
  weekday: React.ReactNode;
  date: React.ReactNode;
  label?: React.ReactNode;
  tone?: WxbStatusTone;
  events?: WxbCalendarStripEvent[];
  disabled?: boolean;
  isToday?: boolean;
}

export interface WxbCalendarStripProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  days: WxbCalendarStripDay[];
  selectedKey?: string;
  defaultSelectedKey?: string;
  onSelect?: (key: string) => void;
  density?: 'comfortable' | 'compact';
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

export const WxbCalendarStrip: React.FC<WxbCalendarStripProps> = ({
  days,
  selectedKey,
  defaultSelectedKey,
  onSelect,
  density = 'comfortable',
  className = '',
  ...props
}) => {
  const [internalKey, setInternalKey] = React.useState(defaultSelectedKey ?? days[0]?.key ?? '');
  const currentKey = selectedKey ?? internalKey;

  const select = (day: WxbCalendarStripDay) => {
    if (day.disabled) return;
    if (selectedKey === undefined) setInternalKey(day.key);
    onSelect?.(day.key);
  };

  return (
    <div className={joinClasses('wxb-calendar-strip', `wxb-calendar-strip-${density}`, className)} {...props}>
      {days.map((day) => (
        <button
          key={day.key}
          type="button"
          className={joinClasses(
            'wxb-calendar-day',
            currentKey === day.key && 'is-selected',
            day.disabled && 'is-disabled',
            day.isToday && 'is-today',
            `wxb-calendar-day-${day.tone ?? 'neutral'}`,
          )}
          disabled={day.disabled}
          onClick={() => select(day)}
        >
          <span className="wxb-calendar-weekday">{day.weekday}</span>
          <span className="wxb-calendar-date">{day.date}</span>
          {day.label && <span className="wxb-calendar-label">{day.label}</span>}
          {day.events && day.events.length > 0 && (
            <span className="wxb-calendar-events">
              {day.events.slice(0, 3).map((event) => (
                <span
                  key={event.key}
                  className={joinClasses('wxb-calendar-event', `wxb-calendar-event-${event.tone ?? 'neutral'}`)}
                >
                  {event.label}
                </span>
              ))}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

